import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatBytes } from '@/shared/lib/format-bytes';
import { Button } from '@/shared/ui/button/button';
import type { Attachment } from './attachment';
import { AttachmentSyncMark } from './attachment-sync-mark';
import { AttachmentThumb } from './attachment-thumb';
import { openAttachment } from './open-attachment';

type AttachmentItemProps = {
  readonly attachment: Attachment;
  readonly onRemove: () => void;
};

export function AttachmentItem({ attachment, onRemove }: AttachmentItemProps) {
  const { t } = useTranslation();
  async function open() {
    if (!(await openAttachment(attachment)))
      toast.info(t('This file has not been downloaded to this device yet'));
  }

  return (
    <li className="flex items-center gap-3 px-3 py-2">
      <AttachmentThumb attachment={attachment} />
      <button
        type="button"
        className="flex min-w-0 flex-1 flex-col items-start text-left"
        onClick={() => void open()}
      >
        <span className="w-full truncate text-sm font-medium hover:underline">{attachment.name}</span>
        <span className="text-xs text-muted-foreground">{formatBytes(attachment.size)}</span>
        <AttachmentSyncMark sha256={attachment.sha256} />
      </button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('Remove {name}', { name: attachment.name })}
        onClick={onRemove}
      >
        <X />
      </Button>
    </li>
  );
}
